<template>
    <v-dialog v-model="show" fullscreen transition="slide-y-transition" overlay-color="#111" overlay-opacity="0.95">
        <div class="dashboard-container">
            <!-- Header -->
            <header class="dashboard-header">
                <div class="header-content">
                    <button class="back-button" @click="close">
                        <span>←</span>
                    </button>
                    <div class="header-title">
                        <span class="breadcrumb">Analytics / </span>
                        <span class="page-title">Dashboards</span>
                    </div>
                </div>
            </header>

            <!-- Navigation Tabs Section -->
            <div class="nav-section">
                <nav class="nav-tabs-container">
                    <div class="nav-tabs">
                        <button v-for="tab in tabs" :key="tab.id" :class="['nav-tab', { active: activeTab === tab.id }]"
                            @click="activeTab = tab.id">
                            {{ tab.name }}
                        </button>
                    </div>
                </nav>
            </div>

            <div class="dashboard-content">
                <!-- Sidebar KPIs -->
                <aside class="sidebar">
                    <h3>KPI's for Dashboards</h3>
                    <ul class="kpi-list">
                        <li v-for="kpi in kpis" :key="kpi.id" :class="['kpi-item', { active: kpi.active }]">
                            <v-icon :icon="kpi.icon" size="18" />
                            <span>{{ kpi.name }}</span>
                        </li>
                    </ul>
                </aside>

                <!-- Main Dashboard -->
                <main class="main-content">
                    <!-- Top Row Widgets -->
                    <div class="widgets-row">
                        <div class="widget energy-intensity">
                            <h4>Energy Intensity</h4>
                            <div class="metric">
                                <span class="value">47</span>
                                <span class="unit">(kWh/sqm)</span>
                            </div>
                            <div class="chart-mini">
                                <apexchart :options="energyIntensityChart.options" :series="energyIntensityChart.series"
                                    type="bar" height="40" />
                            </div>
                        </div>

                        <div class="widget cost-change">
                            <h4>Change in Cost</h4>
                            <div class="metric change-positive">
                                <span class="icon">▲</span>
                                <span class="value">5.2%</span>
                            </div>
                            <div class="chart-mini">
                                <apexchart :options="costChangeChart.options" :series="costChangeChart.series"
                                    type="bar" height="40" />
                            </div>
                        </div>

                        <div class="widget cost-prediction">
                            <h4>Cost Prediction</h4>
                            <div class="chart-container">
                                <apexchart :options="costPredictionChart.options" :series="costPredictionChart.series"
                                    type="line" height="70" />
                            </div>
                        </div>

                        <div class="widget activity-appliances">
                            <h4>Activity Appliances</h4>
                            <p class="appliance-text">Top 3 appliances make up</p>
                            <div class="metric">
                                <span class="value">70.3%</span>
                                <span class="unit">of the net</span>
                            </div>
                            <div class="chart-mini">
                                <apexchart :options="applianceChart.options" :series="applianceChart.series" type="line"
                                    height="40" />
                            </div>
                        </div>
                    </div>

                    <!-- Bottom Row Charts -->
                    <div class="charts-row">
                        <div class="chart-container energy-consumption">
                            <div class="chart-header">
                                <h4>Energy Consumption</h4>
                                <span class="unit">(kWh/sqm)</span>
                                <div class="benchmark">
                                    <span class="benchmark-label">Benchmark</span>
                                    <span class="benchmark-value">70.0</span>
                                </div>
                            </div>
                            <apexchart :options="energyConsumptionChart.options" :series="energyConsumptionChart.series"
                                type="bar" height="300" />
                            <div class="chart-legend">
                                <span class="legend-item above-norm">■ Above the norm (energy consumption is too
                                    high)</span>
                            </div>
                        </div>

                        <div class="chart-container active-spaces">
                            <h4>Active Spaces</h4>
                            <div class="donut-container">
                                <apexchart :options="activeSpacesChart.options" :series="activeSpacesChart.series"
                                    type="donut" height="300" />
                                <div class="center-label">
                                    <span class="center-text">Office</span>
                                </div>
                            </div>
                            <p class="chart-description">Compare ESG scores across properties and monitor improvement
                            </p>
                        </div>
                    </div>

                    <!-- Usage Estimate Chart -->
                    <div class="usage-estimate">
                        <h4>Usage Estimate</h4>
                        <apexchart :options="usageEstimateChart.options" :series="usageEstimateChart.series" type="bar"
                            :height="getUsageChartHeight()" />
                    </div>
                </main>
            </div>
        </div>
    </v-dialog>
</template>

<script setup>
import { ref, defineExpose, onMounted, onUnmounted } from 'vue'

const show = ref(false);
function open() { show.value = true; }
function close() { show.value = false; }

defineExpose({ open, close });

const activeTab = ref(1)
const screenWidth = ref(window.innerWidth)

function getUsageChartHeight() {
    if (screenWidth.value <= 450) return 200
    if (screenWidth.value <= 768) return 250
    return 300
}

function updateScreenWidth() {
    screenWidth.value = window.innerWidth
}

onMounted(() => {
    window.addEventListener('resize', updateScreenWidth)
})

onUnmounted(() => {
    window.removeEventListener('resize', updateScreenWidth)
})

const tabs = ref([
    { id: 1, name: 'Real Estate Owners' },
    { id: 2, name: 'Renters (Tenants)' },
    { id: 3, name: 'Facility Management (CAFM)' },
    { id: 4, name: 'Asset Managers' }
])

const kpis = ref([
    { id: 1, name: 'Energy Consumption', icon: 'mdi-lightning-bolt', active: true },
    { id: 2, name: 'Carbon Emissions', icon: 'mdi-cloud', active: false },
    { id: 3, name: 'ESG Score', icon: 'mdi-trending-up', active: false },
    { id: 4, name: 'Occupancy Rate', icon: 'mdi-account-group', active: false },
    { id: 5, name: 'Operational Cost', icon: 'mdi-currency-usd', active: false }
])

// Chart configurations
const energyIntensityChart = ref({
    series: [{
        data: [30, 40, 35, 45, 42, 38]
    }],
    options: {
        chart: {
            type: 'bar',
            sparkline: { enabled: true },
            toolbar: { show: false }
        },
        colors: ['#4299e1'],
        plotOptions: {
            bar: {
                columnWidth: '60%',
                distributed: false,
                borderRadius: 2
            }
        }
    }
})

const costChangeChart = ref({
    series: [{
        data: [20, 30, 35, 25]
    }],
    options: {
        chart: {
            type: 'bar',
            sparkline: { enabled: true },
            toolbar: { show: false }
        },
        colors: ['#48bb78'],
        plotOptions: {
            bar: {
                columnWidth: '60%',
                borderRadius: 2
            }
        }
    }
})

const costPredictionChart = ref({
    series: [{
        data: [30, 40, 35, 50, 49, 60, 70, 85, 95]
    }],
    options: {
        chart: {
            type: 'line',
            sparkline: { enabled: true },
            toolbar: { show: false }
        },
        stroke: {
            curve: 'smooth',
            width: 3
        },
        colors: ['#4fd1c7']
    }
})

const applianceChart = ref({
    series: [{
        data: [20, 30, 45, 35, 50, 45]
    }],
    options: {
        chart: {
            type: 'line',
            sparkline: { enabled: true },
            toolbar: { show: false }
        },
        stroke: {
            curve: 'smooth',
            width: 3
        },
        colors: ['#ffffff']
    }
})

const energyConsumptionChart = ref({
    series: [{
        name: 'Current Period',
        data: [65, 0]
    }, {
        name: 'Previous Period',
        data: [0, 95]
    }],
    options: {
        chart: {
            type: 'bar',
            toolbar: { show: false },
            background: 'transparent'
        },
        colors: ['#48bb78', '#e53e3e'],
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '60%',
                endingShape: 'rounded',
                borderRadius: 4
            }
        },
        dataLabels: {
            enabled: false
        },
        xaxis: {
            categories: ['Period 1', 'Period 2'],
            labels: {
                style: {
                    colors: '#a0aec0',
                    fontSize: '12px'
                }
            },
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            }
        },
        yaxis: {
            max: 160,
            labels: {
                style: {
                    colors: '#a0aec0',
                    fontSize: '12px'
                }
            }
        },
        grid: {
            borderColor: '#374151',
            strokeDashArray: 3
        },
        legend: {
            show: false
        },
        annotations: {
            yaxis: [{
                y: 70,
                borderColor: '#48bb78',
                borderWidth: 2,
                strokeDashArray: 0,
                label: {
                    borderColor: '#48bb78',
                    style: {
                        color: '#fff',
                        background: '#48bb78',
                        fontSize: '11px'
                    },
                    text: 'Benchmark 70.0'
                }
            }]
        }
    }
})

const activeSpacesChart = ref({
    series: [65, 20, 10, 5],
    options: {
        chart: {
            type: 'donut'
        },
        colors: ['#4299e1', '#48bb78', '#ed8936', '#ecc94b'],
        labels: ['Open Office 65%', 'Meeting Room 20%', 'Lobby 10%', 'Rooms 5%'],
        legend: {
            position: 'bottom',
            labels: {
                colors: '#a0aec0',
                useSeriesColors: false
            },
            fontSize: '12px',
            itemMargin: {
                horizontal: 8,
                vertical: 4
            }
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '65%',
                    labels: {
                        show: false
                    }
                }
            }
        },
        dataLabels: {
            enabled: false
        },
        responsive: [{
            breakpoint: 768,
            options: {
                legend: {
                    fontSize: '10px'
                }
            }
        }]
    }
})

const usageEstimateChart = ref({
    series: [{
        name: 'Electricity',
        data: [25, 30, 35, 15, 40, 45, 20, 25, 20, 50, 60]
    }, {
        name: 'Gas',
        data: [10, 15, 20, 8, 18, 22, 12, 15, 12, 25, 30]
    }],
    options: {
        chart: {
            type: 'bar',
            stacked: true,
            toolbar: { show: false },
            background: 'transparent',
            height: 300
        },
        colors: ['#4fd1c7', '#ed8936'],
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '70%',
                borderRadius: 2
            }
        },
        xaxis: {
            categories: ['0 hrs', '1 hrs', '2 hrs', '3 hrs', '4 hrs', '5 hrs', '6 hrs', '7 hrs', '8 hrs', '9 hrs', '10 hrs'],
            labels: {
                style: {
                    colors: '#a0aec0',
                    fontSize: '11px'
                }
            },
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: '#a0aec0',
                    fontSize: '11px'
                }
            }
        },
        legend: {
            position: 'bottom',
            labels: {
                colors: '#a0aec0'
            },
            fontSize: '12px',
            itemMargin: {
                horizontal: 10
            }
        },
        grid: {
            borderColor: '#374151',
            strokeDashArray: 3
        },
        responsive: [{
            breakpoint: 768,
            options: {
                chart: {
                    height: 250
                },
                legend: {
                    fontSize: '10px'
                },
                xaxis: {
                    labels: {
                        style: {
                            fontSize: '9px'
                        }
                    }
                }
            }
        }, {
            breakpoint: 450,
            options: {
                chart: {
                    height: 200
                },
                legend: {
                    fontSize: '9px'
                },
                xaxis: {
                    labels: {
                        style: {
                            fontSize: '8px'
                        }
                    }
                }
            }
        }]
    }
})

</script>

<style scoped>
.dashboard-container {
    height: 100vh;
    height: 100dvh;
    background: #1a202c;
    color: #ffffff;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.dashboard-header {
    background: #2d3748;
    border-bottom: 1px solid #4a5568;
    padding: 0;
    flex-shrink: 0;
}

.header-content {
    display: flex;
    align-items: center;
    padding: 1rem 1.5rem;
    gap: 1rem;
}

.back-button {
    background: none;
    border: none;
    color: #a0aec0;
    font-size: 1.25rem;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 4px;
    transition: all 0.3s ease;
}

.back-button:hover {
    background: #374151;
    color: #ffffff;
}

.header-title {
    font-size: 1rem;
}

.breadcrumb {
    color: #a0aec0;
}

.page-title {
    color: #ffffff;
    font-weight: 600;
}

.nav-section {
    background: #1a202c;
    padding: 1rem 1.5rem 0.5rem;
    display: flex;
    justify-content: flex-end;
    border-bottom: 1px solid #4a5568;
}

.nav-tabs-container {
    flex-shrink: 0;
}

.nav-tabs {
    display: flex;
    background: #374151;
    padding: 0.25rem;
    border-radius: 12px;
    border: 1px solid #4a5568;
    gap: 0.25rem;
}

.nav-tab {
    padding: 0.5rem 1rem;
    background: none;
    border: none;
    color: #a0aec0;
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 8px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
}

.nav-tab:hover {
    color: #ffffff;
    background: #4a5568;
}

.nav-tab.active {
    color: #4299e1;
    background: #1a202c;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.dashboard-content {
    display: flex;
    flex: 1;
    min-height: 0;
    overflow: hidden;
}

.sidebar {
    width: 280px;
    background: #2d3748;
    padding: 2rem 1.5rem;
    border-right: 1px solid #4a5568;
    overflow-y: auto;
    flex-shrink: 0;
}

.sidebar h3 {
    margin: 0 0 2rem 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #ffffff;
}

.kpi-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.kpi-item {
    display: flex;
    align-items: center;
    padding: 1rem;
    margin-bottom: 0.5rem;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 0.875rem;
}

.kpi-item:hover {
    background: #374151;
}

.kpi-item.active {
    background: #3182ce;
    color: #ffffff;
}

.kpi-item .v-icon {
    margin-right: 0.75rem;
    flex-shrink: 0;
}

.main-content {
    flex: 1;
    padding: 2rem;
    padding-bottom: 3rem;
    overflow-y: auto;
    background: #1a202c;
    min-height: 0;
}

.widgets-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.widget {
    background: #2d3748;
    border-radius: 8px;
    /* padding: 1rem; */
    border: 1px solid #4a5568;
    min-height: 100px;
    display: flex;
    flex-direction: column;
    position: relative;
}

.widget h4 {
    margin: 0 0 0.5rem 0;
    font-size: 0.6875rem;
    color: #a0aec0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
    line-height: 1.2;
}

.metric {
    display: flex;
    align-items: baseline;
    margin-bottom: 0.5rem;
    flex-wrap: wrap;
}

.metric .value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #ffffff;
    line-height: 1;
}

.metric .unit {
    font-size: 0.6875rem;
    color: #a0aec0;
    margin-left: 0.25rem;
}

.change-positive {
    color: #48bb78;
}

.change-positive .icon {
    color: #48bb78;
    margin-right: 0.25rem;
    font-size: 1rem;
}

.activity-appliances {
    background: linear-gradient(135deg, #4299e1, #3182ce);
    color: #ffffff;
}

.activity-appliances h4 {
    color: rgba(255, 255, 255, 0.9);
}

.appliance-text {
    font-size: 0.6875rem;
    margin: 0 0 0.25rem 0;
    opacity: 0.9;
    line-height: 1.2;
}

.chart-mini {
    margin-top: auto;
    height: 40px;
}

.charts-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    margin-bottom: 2rem;
}

.chart-container {
    background: #2d3748;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #4a5568;
}

.chart-header {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
    gap: 1rem;
    flex-wrap: wrap;
}

.chart-header h4 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #ffffff;
}

.unit {
    font-size: 0.75rem;
    color: #a0aec0;
}

.benchmark {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.benchmark-label {
    font-size: 0.75rem;
    color: #48bb78;
}

.benchmark-value {
    font-size: 0.75rem;
    font-weight: 600;
    color: #48bb78;
}

.chart-legend {
    margin-top: 1rem;
}

.legend-item {
    font-size: 0.75rem;
    color: #a0aec0;
}

.above-norm {
    color: #e53e3e;
}

.donut-container {
    position: relative;
}

.center-label {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
}

.center-text {
    font-size: 1.25rem;
    font-weight: 600;
    color: #ffffff;
}

.chart-description {
    font-size: 0.75rem;
    color: #a0aec0;
    margin-top: 1rem;
    text-align: center;
    line-height: 1.4;
}

.usage-estimate {
    background: #2d3748;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #4a5568;
    margin-bottom: 2rem;
}

.usage-estimate h4 {
    margin: 0 0 1rem 0;
    font-size: 1rem;
    font-weight: 600;
    color: #ffffff;
}

/* Responsive Design */
@media (max-width: 1200px) {
    .widgets-row {
        grid-template-columns: repeat(2, 1fr);
    }

    .main-content {
        padding: 1.5rem;
    }
}

@media (max-width: 968px) {
    .dashboard-content {
        flex-direction: column;
    }

    .sidebar {
        width: 100%;
        order: 2;
        max-height: 200px;
        padding: 1rem;
        border-right: none;
        border-top: 1px solid #4a5568;
    }

    .main-content {
        order: 1;
        padding: 1.5rem;
        padding-bottom: 2rem;
        flex: 1;
    }

    .charts-row {
        grid-template-columns: 1fr;
        gap: 1.5rem;
    }

    .header-content {
        padding: 0.75rem 1rem;
    }

    .nav-section {
        padding: 0.75rem 1rem;
        justify-content: center;
    }

    .nav-tabs-container {
        width: 100%;
        overflow-x: auto;
        scrollbar-width: none;
        -ms-overflow-style: none;
    }

    .nav-tabs-container::-webkit-scrollbar {
        display: none;
    }

    .nav-tabs {
        width: max-content;
        min-width: 100%;
    }

    .nav-tab {
        padding: 0.5rem 0.875rem;
        font-size: 0.6875rem;
        flex-shrink: 0;
    }
}

@media (max-width: 768px) {
    .widgets-row {
        grid-template-columns: 1fr;
        gap: 0.75rem;
    }

    .main-content {
        padding: 1rem;
        padding-bottom: 2rem;
    }

    .sidebar {
        padding: 1rem;
        max-height: 180px;
    }

    .widget {
        padding: 0.875rem;
        min-height: 90px;
    }

    .chart-container {
        padding: 1rem;
    }

    .usage-estimate {
        padding: 1rem;
        margin-bottom: 1.5rem;
    }

    .metric .value {
        font-size: 1.25rem;
    }

    .chart-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
    }

    .benchmark {
        margin-left: 0;
    }

    .header-content {
        padding: 0.5rem 1rem;
    }

    .header-title {
        font-size: 0.875rem;
    }

    .nav-section {
        padding: 0.5rem 1rem;
    }

    .nav-tab {
        padding: 0.375rem 0.75rem;
        font-size: 0.625rem;
    }
}

@media (max-width: 450px) {
    .nav-tab {
        padding: 0.375rem 0.625rem;
        font-size: 0.625rem;
    }

    .metric .value {
        font-size: 1.125rem;
    }

    .charts-row {
        gap: 1rem;
    }

    .widgets-row {
        gap: 0.75rem;
    }

    .main-content {
        padding: 0.75rem;
        padding-bottom: 1.5rem;
    }

    .widget {
        padding: 0.75rem;
        min-height: 80px;
    }

    .chart-container {
        padding: 0.875rem;
    }

    .sidebar {
        padding: 0.75rem;
        max-height: 160px;
    }

    .kpi-item {
        padding: 0.75rem;
        font-size: 0.8125rem;
    }

    .kpi-item .v-icon {
        margin-right: 0.625rem;
    }

    .header-content {
        padding: 0.5rem 0.75rem;
    }

    .nav-section {
        padding: 0.5rem 0.75rem;
    }
}

@media (max-width: 355px) {
    .main-content {
        padding: 0.5rem;
        padding-bottom: 1.5rem;
    }

    .sidebar {
        padding: 0.5rem;
        max-height: 140px;
    }

    .widget {
        padding: 0.625rem;
        min-height: 75px;
    }

    .chart-container {
        padding: 0.75rem;
    }

    .usage-estimate {
        padding: 0.75rem;
        margin-bottom: 1rem;
    }

    .nav-tab {
        padding: 0.25rem 0.5rem;
        font-size: 0.5625rem;
    }

    .metric .value {
        font-size: 1rem;
    }

    .widget h4 {
        font-size: 0.625rem;
        margin-bottom: 0.5rem;
    }

    .kpi-item {
        padding: 0.5rem;
        font-size: 0.75rem;
        margin-bottom: 0.25rem;
    }

    .kpi-item .v-icon {
        margin-right: 0.5rem;
    }

    .appliance-text {
        font-size: 0.625rem;
    }

    .center-text {
        font-size: 1rem;
    }

    .header-content {
        padding: 0.5rem;
    }

    .nav-section {
        padding: 0.375rem 0.5rem;
    }

    .header-title {
        font-size: 0.75rem;
    }

    .back-button {
        font-size: 1rem;
        padding: 0.25rem;
    }
}

/* Custom scrollbar for navigation tabs */
.nav-tabs-container {
    scrollbar-width: thin;
    scrollbar-color: #4a5568 transparent;
}

.nav-tabs-container::-webkit-scrollbar {
    height: 4px;
}

.nav-tabs-container::-webkit-scrollbar-track {
    background: transparent;
}

.nav-tabs-container::-webkit-scrollbar-thumb {
    background: #4a5568;
    border-radius: 2px;
}

.nav-tabs-container::-webkit-scrollbar-thumb:hover {
    background: #718096;
}

/* Additional small screen optimizations */
@media (max-width: 320px) {
    .dashboard-container {
        font-size: 0.875rem;
    }

    .widgets-row {
        gap: 0.75rem;
    }

    .charts-row {
        gap: 0.75rem;
    }

    .header-content {
        padding: 0.25rem 0.5rem;
    }
}

/* Prevent horizontal scroll */
@media (max-width: 968px) {
    .dashboard-container {
        overflow-x: hidden;
    }
}

/* Custom scrollbar for webkit browsers */
.sidebar::-webkit-scrollbar,
.main-content::-webkit-scrollbar {
    width: 6px;
}

.sidebar::-webkit-scrollbar-track,
.main-content::-webkit-scrollbar-track {
    background: #2d3748;
}

.sidebar::-webkit-scrollbar-thumb,
.main-content::-webkit-scrollbar-thumb {
    background: #4a5568;
    border-radius: 3px;
}

.sidebar::-webkit-scrollbar-thumb:hover,
.main-content::-webkit-scrollbar-thumb:hover {
    background: #718096;
}
</style>